import React from "react";
import { useTranslation } from "react-i18next";
import { classNames } from "primereact/utils";
import ArticleSectionViewer from "../../../Pages/KnowledgeBase/ArticleSectionViewer";
import { filterKnowledgeArticleSections } from "../../../Pages/KnowledgeBase/knowledgeArticleRelationConstants";
import { KnowledgeArticleEmbedState, useKnowledgeArticleEmbed } from "./useKnowledgeArticleEmbed";
import "./KnowledgeArticleEmbedBody.scss";

interface KnowledgeArticleEmbedContentProps {
    embed: KnowledgeArticleEmbedState;
    compact?: boolean;
    sectionIdentifiers?: string[] | null;
}

export const KnowledgeArticleEmbedContent: React.FC<KnowledgeArticleEmbedContentProps> = ({
    embed,
    compact = false,
    sectionIdentifiers,
}) => {
    const { t } = useTranslation();
    const { article, mediaAssets, loading, failed } = embed;
    const visibleSections = filterKnowledgeArticleSections(article?.sections ?? [], sectionIdentifiers);

    return (
        <div className={classNames("knowledge-article-embed-body", compact && "knowledge-article-embed-body--compact")}>
            {loading && (
                <p className="knowledge-article-embed-body__status">
                    <i className="pi pi-spinner pi-spin" aria-hidden />
                    {t("common.loading")}
                </p>
            )}
            {failed && <p className="knowledge-article-embed-body__status">{t("knowledgeBase.notAvailable")}</p>}
            {!loading && article && visibleSections.length === 0 && (
                <p className="knowledge-article-embed-body__status">{t("knowledgeBase.noContent")}</p>
            )}
            {!loading && article && visibleSections.map((section) => (
                <ArticleSectionViewer
                    key={section.uuid}
                    section={section}
                    mediaAssets={mediaAssets}
                    mediaLoading={loading}
                />
            ))}
        </div>
    );
};

interface KnowledgeArticleEmbedBodyProps {
    articleUuid: string | null;
    compact?: boolean;
    sectionIdentifiers?: string[] | null;
}

const KnowledgeArticleEmbedBody: React.FC<KnowledgeArticleEmbedBodyProps> = ({
    articleUuid,
    compact = false,
    sectionIdentifiers,
}) => {
    const embed = useKnowledgeArticleEmbed(articleUuid);
    return <KnowledgeArticleEmbedContent embed={embed} compact={compact} sectionIdentifiers={sectionIdentifiers} />;
};

export default KnowledgeArticleEmbedBody;
